import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';

import { GoogleDriveAppSelectorIcon } from '@atlassian/rovo-connectors-config/icons';

import { LoadableLazy } from '@confluence/loadable';

const UnconnectedConnectorHoverCardLoadable = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UnconnectedConnectorHoverCard" */ '../UnconnectedConnectorHoverCard.tsx'
			)
		).UnconnectedConnectorHoverCard,
});

const i18n = defineMessages({
	message: {
		id: 'byline-connections.menu.footer.connect-placeholder',
		defaultMessage: 'Connect Google drive to see rich previews across Confluence',
		description:
			'Promotional message in the Connections byline menu footer prompting the user to connect their Google Drive account',
	},
	connectAction: {
		id: 'byline-connections.menu.footer.connect-action',
		defaultMessage: 'Connect',
		description: 'Call-to-action label next to the message in the Connections byline menu footer',
	},
	connectingAction: {
		id: 'byline-connections.menu.footer.connecting-action',
		defaultMessage: 'Connecting...',
		description:
			'Status label shown in place of the Connect call-to-action in the Connections byline menu footer while the Google Drive account is being connected',
	},
	dismissAction: {
		id: 'byline-connections.menu.footer.dismiss-action',
		defaultMessage: 'Dismiss',
		description: 'Aria-label for the dismiss button in the Connections byline menu footer',
	},
	googleDriveIconAlt: {
		id: 'byline-connections.menu.footer.google-drive-icon.alt',
		defaultMessage: 'Google Drive',
		description: 'Alt text for the Google Drive icon shown in the byline menu footer banner',
	},
});

const styles = cssMap({
	footer: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border'),
	},
	message: {
		font: token('font.body.small'),
		color: token('color.text'),
	},
	connect: {
		font: token('font.body'),
		color: token('color.link'),
		backgroundColor: 'transparent',
		borderStyle: 'none',
		padding: token('space.0'),
		cursor: 'pointer',
		flexShrink: 0,
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:focus-visible': {
			textDecoration: 'underline',
		},
	},
	connecting: {
		font: token('font.body'),
		color: token('color.text.disabled'),
		backgroundColor: 'transparent',
		borderStyle: 'none',
		padding: token('space.0'),
		cursor: 'default',
		flexShrink: 0,
		textDecoration: 'none',
	},
});

export interface GdriveConnectorPromoBannerProps {
	onConnect: () => void;
	onDismiss: () => void;
	isConnecting?: boolean;
}

export const GdriveConnectorPromoBanner = ({
	onConnect,
	onDismiss,
	isConnecting,
}: GdriveConnectorPromoBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isBannerHovered, setIsBannerHovered] = useState(false);

	return (
		<UnconnectedConnectorHoverCardLoadable providerKey="gdrive" isOpen={isBannerHovered}>
			{({ ref, ...triggerProps }) => (
				<Box
					ref={ref as React.Ref<HTMLElement>}
					{...triggerProps}
					xcss={styles.footer}
					onMouseEnter={() => setIsBannerHovered(true)}
					onMouseLeave={() => setIsBannerHovered(false)}
					onFocus={() => setIsBannerHovered(true)}
					onBlur={() => setIsBannerHovered(false)}
				>
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							<Tile
								label={formatMessage(i18n.googleDriveIconAlt)}
								size="small"
								backgroundColor="color.background.neutral"
								isInset={false}
								hasBorder
							>
								<GoogleDriveAppSelectorIcon />
							</Tile>
							<Box xcss={styles.message}>{formatMessage(i18n.message)}</Box>
						</Inline>
						<Inline space="space.050" alignBlock="center">
							<Pressable
								xcss={isConnecting ? styles.connecting : styles.connect}
								onClick={onConnect}
								isDisabled={isConnecting}
								aria-busy={isConnecting}
							>
								{formatMessage(isConnecting ? i18n.connectingAction : i18n.connectAction)}
							</Pressable>
							<IconButton
								appearance="subtle"
								spacing="compact"
								icon={CrossIcon}
								label={formatMessage(i18n.dismissAction)}
								onClick={onDismiss}
							/>
						</Inline>
					</Inline>
				</Box>
			)}
		</UnconnectedConnectorHoverCardLoadable>
	);
};
